import { Grid, Box, Typography, Divider, Stack } from '@mui/material';
import { useField } from 'react-final-form';

import { TextField, Switches } from '@occc-app/design-system';
import { Tooltip } from '@occc/ui-design-system';

import { isFloat } from '../utils';

// Shown when the RJR Auto Offers toggle is disabled to explain why the location can't be enabled.
const RJR_INELIGIBLE_TOOLTIP =
  "This location can't be enabled for RJR - it's missing a valid RBDS code or hasn't been provisioned for RJR yet";

type LocationScanDataFormContentProps = {
  // A location is RJR-eligible when it has a provisioned RJR RBDS code. When it isn't,
  // the RJR Auto Offers toggle is disabled/blocked (AC8).
  isRjrEligible?: boolean;
};

// Renders a Switches toggle with an Enabled/Disabled caption. Shared layout for the
// per-manufacturer toggles (Altria P+, RJR Auto Offers).
const ToggleWithCaption = ({
  name,
  label,
  disabled = false,
}: {
  name: string;
  label: string;
  disabled?: boolean;
}) => {
  const {
    input: { value },
  } = useField(name, { subscription: { value: true } });

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
      <Switches
        name={name}
        data={{ disabled }}
        switchProps={{ inputProps: { 'aria-label': label } }}
      />
      <Box>
        <Typography variant='caption' color='text.secondary' display='block'>
          {label}
        </Typography>
        <Typography variant='subtitle1'>{value ? 'Enabled' : 'Disabled'}</Typography>
      </Box>
    </Box>
  );
};

const AltriaPPlusToggle = () => (
  // Altria P+ eligibility gating (AGDC Store Profile API) is tracked separately in OCST-23230,
  // so the toggle renders plainly here.
  <ToggleWithCaption name='altriaPPlusApiEnabled' label='Altria P+' />
);

const RjrAutoOffersToggle = ({ isRjrEligible }: { isRjrEligible: boolean }) => {
  const toggle = (
    <ToggleWithCaption
      name='rjrAutoOffersEnabled'
      label='RJR Auto Offers'
      disabled={!isRjrEligible}
    />
  );

  if (isRjrEligible) {
    return toggle;
  }

  // A disabled MUI Switch has `pointer-events: none`, so the tooltip must fire on an enabled
  // wrapper element. inline-block keeps the hit area tight over the control; tabIndex makes the
  // reason reachable by keyboard (matches the disabled-control pattern in
  // LocationUpdateFormRenderComponent.tsx).
  return (
    <Tooltip title={RJR_INELIGIBLE_TOOLTIP} placement='top'>
      <Box sx={{ display: 'inline-block' }} tabIndex={0}>
        {toggle}
      </Box>
    </Tooltip>
  );
};

const LocationScanDataFormContent = ({
  isRjrEligible = false,
}: LocationScanDataFormContentProps) => (
  <Stack divider={<Divider />} spacing={3}>
    <Box>
      <Typography variant='h6' mb={3}>
        Altria
      </Typography>
      <Grid container columnSpacing={3} rowSpacing={1}>
        <Grid item xs={6} sm={3}>
          <TextField
            name='altriaMan'
            label='Altria MAN'
            margin='normal'
            fieldProps={{
              validate: isFloat,
            }}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField
            name='altriaRcn'
            label='Altria RCN'
            margin='normal'
            fieldProps={{
              validate: isFloat,
            }}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <AltriaPPlusToggle />
        </Grid>
      </Grid>
    </Box>
    <Box>
      <Typography variant='h6' mb={3}>
        RJR
      </Typography>
      <Grid container columnSpacing={3} rowSpacing={1}>
        <Grid item xs={6} sm={3}>
          <TextField name='rjrRbdsCode' label='RJR RBDS Code' margin='normal' />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField name='rjrAccountNumber' label='RJR Account Number' margin='normal' />
        </Grid>
        <Grid item xs={6} sm={3}>
          <RjrAutoOffersToggle isRjrEligible={isRjrEligible} />
        </Grid>
      </Grid>
    </Box>
  </Stack>
);

export default LocationScanDataFormContent;
